<template>
  <v-container class="vera-admin py-10 py-md-14">
    <div class="vera-admin__header">
      <div>
        <p class="vera-admin__eyebrow">Reckon Administration</p>
        <h1>Vera Knowledge</h1>
        <p>
          Add approved information that Vera may share even when it does not appear on a website
          page.
        </p>
      </div>
      <v-btn v-if="isAdmin" color="red" large @click="startNewEntry">
        <v-icon left>mdi-plus</v-icon>
        New entry
      </v-btn>
    </div>

    <v-alert v-if="!authReady" type="info" outlined>Checking your administrator access…</v-alert>
    <v-alert v-else-if="!isAuthenticated" type="info" outlined>
      Sign in with an approved Reckon administrator account to continue.
      <div class="mt-4"><v-btn color="red" :to="{ name: 'login' }">Sign in</v-btn></div>
    </v-alert>
    <v-alert v-else-if="!isAdmin" type="error" outlined>
      This account is not authorized to manage Vera’s knowledge.
    </v-alert>

    <template v-else>
      <v-alert v-if="pageError" class="mt-6" type="error" outlined>{{ pageError }}</v-alert>

      <v-row class="mt-4">
        <v-col cols="12" md="5">
          <v-card class="vera-admin__panel" dark outlined>
            <v-card-title>Knowledge entries</v-card-title>
            <v-divider />
            <v-progress-linear v-if="loading" color="red" indeterminate />
            <v-list v-if="entries.length" color="transparent" two-line>
              <v-list-item
                v-for="entry in entries"
                :key="entry.id"
                :class="{ 'vera-admin__entry--active': entry.id === form.id }"
                @click="editEntry(entry)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ entry.title }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ statusLabel(entry.status) }} · {{ formatDate(entry.updatedAt) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-icon>
                  <v-icon :color="entry.status === 'published' ? 'green' : 'grey'">
                    {{ entry.status === 'published' ? 'mdi-earth' : 'mdi-file-document-edit-outline' }}
                  </v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-list>
            <v-card-text v-else-if="!loading" class="vera-admin__empty">
              No knowledge entries yet.
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-card class="vera-admin__panel" dark outlined>
            <v-card-title>{{ form.id ? 'Edit knowledge' : 'New knowledge' }}</v-card-title>
            <v-card-text class="pt-6">
              <v-alert v-if="form.status === 'published'" type="warning" outlined dense>
                Published entries may be quoted by Vera to any public website visitor.
              </v-alert>
              <v-form ref="knowledgeForm" @submit.prevent="saveEntry">
                <v-text-field
                  v-model.trim="form.title"
                  label="Title"
                  maxlength="140"
                  counter
                  outlined
                  required
                  :rules="[requiredRule]"
                />
                <v-textarea
                  v-model="form.body"
                  label="Approved knowledge"
                  hint="Write complete facts in the language Vera should use."
                  persistent-hint
                  maxlength="20000"
                  counter
                  outlined
                  rows="10"
                  required
                  :rules="[requiredRule]"
                />
                <v-combobox
                  v-model="form.tags"
                  label="Tags"
                  hint="Optional. Press Enter after each tag."
                  persistent-hint
                  multiple
                  chips
                  deletable-chips
                  outlined
                  :rules="[tagsRule]"
                />
                <v-text-field
                  v-model.trim="form.sourceUrl"
                  label="Optional source URL"
                  placeholder="https://… or /contact"
                  outlined
                  :rules="[sourceUrlRule]"
                />
                <v-select
                  v-model="form.status"
                  label="Status"
                  :items="statusOptions"
                  outlined
                />

                <v-alert v-if="formError" type="error" outlined>{{ formError }}</v-alert>
                <v-alert v-if="savedMessage" type="success" outlined>{{ savedMessage }}</v-alert>

                <div class="vera-admin__actions">
                  <v-btn color="red" type="submit" :loading="saving">Save entry</v-btn>
                  <v-btn text :disabled="saving" @click="startNewEntry">Clear</v-btn>
                  <v-spacer />
                  <v-btn
                    v-if="form.id && form.status !== 'archived'"
                    color="error"
                    text
                    :loading="archiving"
                    @click="archiveEntry"
                  >
                    Archive
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { isReckonAdmin } from '@/lib/admin/permissions';
import {
  archiveVeraKnowledge,
  listVeraKnowledge,
  saveVeraKnowledge,
} from '@/services/vera';

const emptyForm = () => ({
  id: '',
  title: '',
  body: '',
  tags: [],
  sourceUrl: '',
  status: 'draft',
});

export default {
  name: 'VeraKnowledgeAdminView',
  data: () => ({
    entries: [],
    form: emptyForm(),
    loading: false,
    saving: false,
    archiving: false,
    pageError: '',
    formError: '',
    savedMessage: '',
    statusOptions: [
      { text: 'Draft — not available to Vera', value: 'draft' },
      { text: 'Published — available to all visitors', value: 'published' },
    ],
    requiredRule: (value) => Boolean(String(value || '').trim()) || 'This field is required.',
    tagsRule: (value) => (
      (!value || (value.length <= 10 && value.every((tag) => String(tag).trim().length <= 40)))
        || 'Use at most 10 tags of 40 characters each.'
    ),
    sourceUrlRule: (value) => {
      if (!value) return true;
      if (value.startsWith('/') && !value.startsWith('//')) return true;
      try {
        return new URL(value).protocol === 'https:' || 'Use an HTTPS URL or a website path beginning with /.';
      } catch (error) {
        return 'Use an HTTPS URL or a website path beginning with /.';
      }
    },
  }),
  computed: {
    authReady() {
      return this.$store.getters.authReady;
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    },
    isAdmin() {
      return isReckonAdmin(this.currentUser);
    },
  },
  watch: {
    authReady: {
      immediate: true,
      handler(ready) {
        if (ready && this.isAdmin) this.loadEntries();
      },
    },
  },
  methods: {
    async loadEntries() {
      this.loading = true;
      this.pageError = '';
      try {
        this.entries = await listVeraKnowledge();
      } catch (error) {
        this.pageError = error.message;
      } finally {
        this.loading = false;
      }
    },
    startNewEntry() {
      this.form = emptyForm();
      this.formError = '';
      this.savedMessage = '';
      this.$nextTick(() => this.$refs.knowledgeForm?.resetValidation());
    },
    editEntry(entry) {
      this.form = {
        id: entry.id,
        title: entry.title,
        body: entry.body,
        tags: [...(entry.tags || [])],
        sourceUrl: entry.sourceUrl || '',
        status: entry.status === 'published' ? 'published' : 'draft',
      };
      this.formError = '';
      this.savedMessage = '';
    },
    async saveEntry() {
      if (!this.$refs.knowledgeForm.validate()) return;
      this.saving = true;
      this.formError = '';
      this.savedMessage = '';
      try {
        const saved = await saveVeraKnowledge(this.form);
        const index = this.entries.findIndex((entry) => entry.id === saved.id);
        if (index >= 0) this.entries.splice(index, 1, saved);
        else this.entries.unshift(saved);
        this.editEntry(saved);
        this.savedMessage = saved.status === 'published'
          ? 'Saved and published to Vera.'
          : 'Draft saved.';
      } catch (error) {
        this.formError = error.message;
      } finally {
        this.saving = false;
      }
    },
    async archiveEntry() {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Archive this entry and remove it from Vera’s knowledge?')) return;
      this.archiving = true;
      this.formError = '';
      try {
        await archiveVeraKnowledge(this.form.id);
        this.entries = this.entries.filter((entry) => entry.id !== this.form.id);
        this.startNewEntry();
      } catch (error) {
        this.formError = error.message;
      } finally {
        this.archiving = false;
      }
    },
    statusLabel(status) {
      if (status === 'published') return 'Published';
      if (status === 'archived') return 'Archived';
      return 'Draft';
    },
    formatDate(value) {
      if (!value) return 'Not yet saved';
      return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value));
    },
  },
};
</script>

<style scoped>
.vera-admin {
  color: #fff;
  max-width: 1240px;
  min-height: calc(100vh - 64px);
}

.vera-admin__header {
  align-items: flex-end;
  display: flex;
  gap: 32px;
  justify-content: space-between;
}

.vera-admin__header h1 {
  font-family: "Oswald", sans-serif;
  font-size: clamp(2.8rem, 7vw, 5.4rem);
  line-height: 1;
  margin-bottom: 18px;
  text-transform: uppercase;
}

.vera-admin__header p:not(.vera-admin__eyebrow) {
  color: #c8c8c8;
  max-width: 720px;
}

.vera-admin__eyebrow {
  color: #f02020;
  font-family: "Oswald", sans-serif;
  letter-spacing: .16em;
  text-transform: uppercase;
}

.vera-admin__panel {
  background: #0b0b0b;
  border-color: #343434;
}

.vera-admin__entry--active {
  background: #211010;
  border-left: 3px solid #f02020;
}

.vera-admin__empty {
  color: #aaa;
}

.vera-admin__actions {
  align-items: center;
  display: flex;
  gap: 8px;
}

@media (max-width: 600px) {
  .vera-admin__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .vera-admin__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .vera-admin__actions .v-spacer {
    display: none;
  }
}
</style>
